import { inject, Injectable } from '@angular/core';
import { ChatStoreService } from '../chat-store/chat-store.service';
import { combineLatest, distinctUntilChanged, from, Observable, switchMap, withLatestFrom } from 'rxjs';
import { DisplayItem } from '../../components/sb-client/sb-model/display-item';
import { ChatRoom } from '../../model/chat-room';
import { ChatMessage } from '../../model/chat-message';
import { EventType, KnownMembership, MatrixClient, MatrixEvent } from 'matrix-js-sdk';
import { DisplayInfo } from '../../components/sb-client/sb-model/display-info';
import { MatrixService } from '../matrix/matrix.service';
import { DisplayMessageFile } from '../../components/sb-client/sb-model/display-message-file';
import { DisplayMessage } from '../../components/sb-client/sb-model/display-message';
import { PinnedMessageService } from '../pinMessages/pinned-message.service';
import { ReceiptService } from '../receiptService/receipt.service';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
/**
 * Für RxJS-Architektur benötigter Service.
 * Begründung: Keine AFO, da Architektur-übergreifend.
 */
export class ChatMessageService {
  private readonly chatStoreService = inject(ChatStoreService);
  private readonly matrixService = inject(MatrixService);
  private readonly pinnedMessageService = inject(PinnedMessageService);
  //  für das tatsächlich geänderte MatrixEvent neu berechnet wird.
  messages$: Observable<DisplayItem[]> = combineLatest([
    this.chatStoreService.getCurrentChatRoom$().pipe(distinctUntilChanged()),
    this.pinnedMessageService.pins$
  ]).pipe(
    withLatestFrom(this.matrixService.getClientWithOrigin$()),
    switchMap(([[chatRoom], clientWithOrigin]) => from(this.roomToDisplayItemList(chatRoom, clientWithOrigin.client)))
  );

  // Bei jedem State-Update oder Pin-Änderung werden alle Display-Nachrichten neu berechnet.
  // Eigentlich würde es reichen, nur die betroffene Display-Nachricht neu
  // zu berechnen. Dies ist möglich, würde aber den RxJS-Code verkomplizieren.
  private readonly receiptService = inject(ReceiptService);

  async roomToDisplayItemList(room: ChatRoom | undefined, client: MatrixClient | null): Promise<DisplayItem[]> {
    const userId = client?.getUserId();
    if (!room || !client?.getUserId()) {
      return [];
    }

    const items: (DisplayItem | undefined)[] = [];
    for (const message of room.messages) {
      if (!message.hasOriginalEvent()) {
        continue;
      }
      let di: DisplayItem | undefined;
      switch (message.first.getType()) {
        case EventType.RoomMember:
          if (userId) {
            di = this.roomMemberToDisplayInfo(message.first, userId);
          }
          break;
        case EventType.RoomMessageEncrypted:
        case EventType.RoomMessage:
          if (message.isRedacted()) {
            const redactReason = this.transformRedactReason(message.getRedactReason(), message.first);
            di = new DisplayInfo(
              message.first.getId(),
              redactReason,
              redactReason,
              message.first.localTimestamp,
              message.first.sender!.userId
            );
          } else {
            di = await this.roomMessageToDisplayMessage(message, client);
          }
          break;
        default:
          console.warn('Unerwarteter Nachrichtentyp', message.first);
      }
      if (di) {
        items.push(di);
      }
    }
    return items.filter(di => !!di);
  }

  /********************************************************************************************************************
   * Raum-Mitgliedschafts-Änderungen
   ********************************************************************************************************************/

  /**
   * Interpretiert ein Member-Event und transformiert es in die passende DisplayInfo.
   */
  private roomMemberToDisplayInfo(event: MatrixEvent, userId: string): DisplayInfo | undefined {
    let infotext: DisplayInfo | undefined;
    const content = event.getContent();
    if (content.membership === KnownMembership.Join) {
      const prevMembership = event.getPrevContent().membership;
      // Dies ist auch das erstellerEvent
      if (!prevMembership) {
        infotext = this.createJoinInfo(event, userId);
      }
      // Mit diesem If/else werden die Events gefiltert, die eine Änderung
      // des Displaynamens oder des Avatars kommunizieren.
      else if (event.getPrevContent().membership === KnownMembership.Invite) {
        infotext = this.createJoinInfo(event, userId);
      } else {
        // Hier könnte man sich überlegen, ob man diese Änderungen
        // in der Anzeige noch darstellen möchte oder nicht.
        // ...
      }
    } else if (content.membership === KnownMembership.Invite) {
      infotext = this.createInviteInfo(event, userId);
    } else if (content.membership === KnownMembership.Leave) {
      infotext = this.createLeaveInfo(event, userId);
    } else {
      infotext = new DisplayInfo(
        event.getId(),
        'undefined Event',
        'undefined Event',
        event.localTimestamp,
        event.sender!.userId
      );
    }

    return infotext;
  }

  /**
   * Interpretiert ein JoinEvent und transformiert es in die passende DisplayInfo.
   */
  private createJoinInfo(event: MatrixEvent, userId: string): DisplayInfo {
    const sender = event.sender;
    const displayname = event.getContent().displayname;
    if (!displayname || !sender) {
      throw new Error('Join Event ohne Member');
    }
    const infotext = `${this.mxidToUsername(sender.userId)} ist dem Chat beigetreten.`;
    let personalizedInfotext!: string;
    if (sender.userId === userId) {
      personalizedInfotext = 'Sie sind dem Chat beigetreten.';
    } else {
      personalizedInfotext = infotext;
    }

    return new DisplayInfo(event.getId(), infotext, personalizedInfotext, event.localTimestamp, sender.userId);
  }

  /**
   * Interpretiert ein InviteEvent und transformiert es in die passende DisplayInfo.
   */
  private createInviteInfo(event: MatrixEvent, userId: string): DisplayInfo {
    const sender = event.sender;
    const recipient = event.getStateKey();
    let infotext = '';
    if (!sender || !recipient) {
      throw new Error('Sender oder Empfänger im Invite nicht bestimmt');
    }
    infotext = `${this.mxidToUsername(sender.userId)} hat ${this.mxidToUsername(recipient)} zu diesem Chat eingeladen.`;

    let personalizedInfotext: string;
    if (sender.userId === userId) {
      personalizedInfotext =
        `Sie haben ${this.mxidToUsername(recipient)} zu diesem Chat eingeladen.` +
        ` Sobald der Teilnehmer Ihre Einladung angenommen hat, kann er die Nachrichten sehen, die Sie gesendet haben.`;
    } else if (recipient === userId) {
      personalizedInfotext = `Sie wurden von ${this.mxidToUsername(sender.userId)} zu diesem Chat eingeladen.`;
    } else {
      personalizedInfotext = infotext;
    }

    return new DisplayInfo(event.getId(), infotext, personalizedInfotext, event.localTimestamp, sender.userId);
  }

  /**
   * Erstelle eine Custom RedactReason für Nachrichten, die kein redactReason beinhalten.
   * @param redactReason Grund der Löschung
   * @param message enstprechende MatrixEvent
   * @private
   */
  private transformRedactReason(redactReason: string | undefined, message: MatrixEvent): string {
    if (redactReason) {
      return redactReason;
    }
    const deleteTimestamp = message.getUnsigned().redacted_because!.origin_server_ts;
    return `Die Nachricht wurde gelöscht von ${message.sender?.userId} am ${new Date(deleteTimestamp).toLocaleString(
      'de-DE',
      {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: undefined
      }
    )}.`;
  }

  /**
   * Extrahiert aus der MatrixId den Usernamen.
   */
  private mxidToUsername(mxid: string): string {
    return mxid.split(':')[0].substring(1);
  }

  /**
   * Interpretiert ein LeaveEvent und transformiert es in die passende DisplayInfo.
   */
  private createLeaveInfo(event: MatrixEvent, userId: string): DisplayInfo | undefined {
    const sender = event.sender;
    const stateKey = event.getStateKey();
    let infotext = '';
    let personalizedInfotext!: string;
    if (!sender) {
      throw new Error('Leave Event ohne Member');
    }
    // User hat den Raum selbst verlassen
    if (sender.userId === stateKey) {
      infotext = `${this.mxidToUsername(sender.userId)} hat den Chat verlassen.`;
      if (sender.userId === userId) {
        personalizedInfotext = 'Sie haben den Chat verlassen.';
      } else {
        personalizedInfotext = infotext;
      }
    }
    if (!stateKey) {
      throw new Error('Kick Event ohne Member');
    }
    if (sender.userId !== stateKey) {
      // User wurde aus dem Raum entfernt
      infotext = `${this.mxidToUsername(stateKey)} wurde von ${this.mxidToUsername(sender.userId)} aus diesem Chat entfernt.`;

      if (stateKey === userId) {
        personalizedInfotext = `Sie wurden von ${this.mxidToUsername(sender.userId)} aus diesem Chat entfernt.`;
      } else if (sender.userId === userId) {
        personalizedInfotext = `${this.mxidToUsername(stateKey)} wurde von Ihnen aus diesem Chat entfernt.`;
      } else {
        personalizedInfotext = infotext;
      }
    }

    return new DisplayInfo(event.getId(), infotext, personalizedInfotext, event.localTimestamp, sender.userId);
  }

  /********************************************************************************************************************
   * Original-Chat-Nachricht (d. h. keine Edit-Referenz)
   ********************************************************************************************************************/

  private async roomMessageToDisplayMessage(cm: ChatMessage, client: MatrixClient): Promise<DisplayMessage> {
    const inReplyTo = await this.resolveInReplyTo(cm, client);
    const isPinned = this.pinnedMessageService.isPinned(cm.first.getId());
    const isReadByAll$ = this.receiptService.lastReadByAll$.pipe(map(eventId => eventId === cm.first.getId()));
    const contentInfo = cm.first.getContent()?.['info'];
    return contentInfo
      ? new DisplayMessageFile(cm, isPinned, isReadByAll$, inReplyTo)
      : new DisplayMessage(cm, isPinned, isReadByAll$, inReplyTo);
  }

  private async resolveInReplyTo(cm: ChatMessage, client: MatrixClient): Promise<DisplayMessage | undefined> {
    const replyEventId = cm.first.replyEventId;
    if (!replyEventId) {
      return undefined;
    }
    const roomId = cm.first.getRoomId();
    if (!roomId) {
      return undefined;
    }
    const replyEvent = await this.resolveReplyEvent(client, roomId, replyEventId);
    return replyEvent ? new DisplayMessage(ChatMessage.new(replyEvent)) : undefined;
  }

  private async resolveReplyEvent(
    client: MatrixClient,
    roomId: string,
    eventId: string
  ): Promise<MatrixEvent | undefined> {
    // Erst lokal suchen
    const localEvent = client.getRoom(roomId)?.findEventById(eventId);
    if (localEvent) {
      return localEvent;
    }

    // Vom Server nachladen
    try {
      const raw = await client.fetchRoomEvent(roomId, eventId);
      return new MatrixEvent(raw);
    } catch (e) {
      console.warn('Reply-Event konnte nicht geladen werden', eventId, e);
      return undefined;
    }
  }
}
